import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Alert,
  Pressable,
} from 'react-native';
import { 
  PageContainer, 
  ContentContainer,
  Text, 
  Card, 
  Button,
  Input,
  Theme
} from '../../components/ui';
import { Ionicons } from '@expo/vector-icons';
import { auth, firestore, onAuthStateChanged, collection, getDocs, query, where } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import { mentorChatService, MentorGatewayMessage } from '../../services/mentorChatService';
import { useNavigation } from '@react-navigation/native';
import { FirebaseFunctionsAPI } from '../../services/firebaseFunctionsAPI';
import { resetOnboardingState } from '../../utils/onboardingStorage';
import { abTestManager } from '../../services/experimentService';
import * as Sentry from '@sentry/react-native';
import { sqliteCacheService } from '../../services/sqliteCacheService';

interface DebugResult {
  success: boolean;
  data?: any;
  error?: string;
  duration?: number;
}

interface AuthStatus {
  isAuthenticated: boolean;
  uid?: string;
  email?: string;
  displayName?: string;
  emailVerified?: boolean;
  creationTime?: string;
  lastSignInTime?: string;
}

const DebugScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const [authStatus, setAuthStatus] = useState<AuthStatus>({ isAuthenticated: false });
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<{ [key: string]: DebugResult }>({});
  const [logs, setLogs] = useState<string[]>([]);
  const [abTests, setAbTests] = useState<any[]>([]);

  // Mentor Gateway controls (moved to dedicated screen; keep minimal state)
  const [mentorId, setMentorId] = useState<string>('leadership_coach');
  const [gwMessage, setGwMessage] = useState<string>('Help me with time management');
  const [promptStyle, setPromptStyle] = useState<'brief' | 'detailed' | 'detailed_with_books'>('brief');
  const [responseStyle, setResponseStyle] = useState<'ack' | 'short' | 'normal' | 'long'>('short');
  const [modelPreference, setModelPreference] = useState<'fast' | 'standard' | 'reasoning'>('standard');
  const [responseLanguage, setResponseLanguage] = useState<string>('');
  const [maxOutputTokens, setMaxOutputTokens] = useState<string>('');
  const [schemaText, setSchemaText] = useState<string>('');
  const [streamingText, setStreamingText] = useState<string>('');
  const [streamingJson, setStreamingJson] = useState<any>(null);

  useEffect(() => {
    checkAuthStatus();
    loadABTests();
    
    // Listen for auth changes
    const unsubscribe = onAuthStateChanged?.(auth, () => {
      checkAuthStatus();
    });

    return unsubscribe;
  }, []);

  const loadABTests = async () => {
    try {
      const tests = await abTestManager.getAllTests();
      setAbTests(tests);
    } catch (error) {
      addLog(`Failed to load AB tests: ${error}`);
    }
  };

  const handleVariantChange = async (testName: string, variant: string) => {
    await abTestManager.changeVariant(testName, variant);
    await loadABTests(); // Refresh state
    addLog(`Changed ${testName} to: ${variant}`);
  };

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    setLogs(prev => [logEntry, ...prev].slice(0, 50)); // Keep last 50 logs
    console.log(logEntry);
  };

  const checkAuthStatus = () => {
    const user = auth.currentUser;
    if (user) {
      setAuthStatus({
        isAuthenticated: true,
        uid: user.uid,
        email: user.email || undefined,
        displayName: user.displayName || undefined,
        emailVerified: user.emailVerified,
        creationTime: user.metadata.creationTime,
        lastSignInTime: user.metadata.lastSignInTime,
      });
    } else {
      setAuthStatus({ isAuthenticated: false });
    }
  };

  const runTest = async (testName: string, testFunction: () => Promise<DebugResult>) => {
    setIsLoading(true);
    addLog(`🧪 Starting ${testName}...`);
    
    const startTime = Date.now();
    try {
      const result = await testFunction();
      const duration = Date.now() - startTime;
      
      const finalResult = { ...result, duration };
      setResults(prev => ({ ...prev, [testName]: finalResult }));
      
      if (result.success) {
        addLog(`✅ ${testName} passed (${duration}ms)`);
      } else {
        addLog(`❌ ${testName} failed: ${result.error} (${duration}ms)`);
      }
      
      return finalResult;
    } catch (error: any) {
      const duration = Date.now() - startTime;
      const failResult = { success: false, error: error.message, duration };
      setResults(prev => ({ ...prev, [testName]: failResult }));
      addLog(`💥 ${testName} crashed: ${error.message} (${duration}ms)`);
      return failResult;
    } finally {
      setIsLoading(false);
    }
  };

  // === AUTHENTICATION TESTS ===
  const testAuthStatus = async (): Promise<DebugResult> => {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    try {
      // Test getting ID token
      const idToken = await user.getIdToken(true);
      const tokenPreview = idToken.substring(0, 20) + '...';
      
      return {
        success: true,
        data: {
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
          tokenPreview,
          createdAt: user.metadata.creationTime,
        }
      };
    } catch (error: any) {
      return { success: false, error: `Token validation failed: ${error.message}` };
    }
  };

  const testSignOut = async (): Promise<DebugResult> => {
    try {
      await auth.signOut();
      return { success: true, data: 'Successfully signed out' };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  // === PROFILE TESTS ===
  const testProfileGet = async (): Promise<DebugResult> => {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    const result = await PrivateProfileService.getProfile(user.uid);
    return {
      success: result.success,
      data: result.success ? {
        profileExists: !!result.data,
        id: result.data?.id,
        platform: result.data?.platform,
        appVersion: result.data?.appVersion,
        onboardingComplete: (result.data?.onboardingAnswers?.completedAt ?? 0) > 0,
        booksCount: Object.keys(result.data?.booksProgress || {}).length,
        insightsCount: Object.keys(result.data?.insightsProgress || {}).length,
      } : null,
      error: result.error,
    };
  };

  const testProfileCreate = async (): Promise<DebugResult> => {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    // First verify auth token
    try {
      await user.getIdToken(true);
    } catch (tokenError: any) {
      return { success: false, error: `Auth token invalid: ${tokenError.message}` };
    }

    const result = await PrivateProfileService.initializeUserProfile(user.uid);
    return {
      success: result.success,
      data: result.success ? 'Profile initialized successfully' : null,
      error: result.error,
    };
  };

  const testProfileUpdate = async (): Promise<DebugResult> => {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    const testUpdate = {
      totalPoints: Math.floor(Math.random() * 1000),
    };

    const result = await PrivateProfileService.updateProfile(user.uid, testUpdate);
    return {
      success: result.success,
      data: result.success ? { updatedPoints: testUpdate.totalPoints } : null,
      error: result.error,
    };
  };

  // === AI COMMUNICATION TESTS ===
  const testAIOnboarding = async (): Promise<DebugResult> => {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    try {
      const messages: MentorGatewayMessage[] = [
        { role: 'user', content: 'I just completed onboarding. Create a short summary and a 3-step plan.' }
      ];

      const outputSchema = {
        type: 'object',
        properties: {
          profileSummary: { type: 'string' },
          plan: { type: 'array', items: { type: 'string' }, minItems: 3, maxItems: 3 }
        },
        required: ['profileSummary', 'plan'],
        additionalProperties: false,
      } as const;

      const data = await mentorChatService.sendStructuredRequest<any>(
        messages,
        outputSchema,
        mentorId || 'leadership_coach',
        { promptStyle: 'detailed_with_books', responseStyle: 'normal', modelPreference: 'standard' }
      );

      return {
        success: true,
        data: {
          hasProfileSummary: typeof data?.profileSummary === 'string',
          summaryPreview: String(data?.profileSummary || '').slice(0, 100),
          planItems: Array.isArray(data?.plan) ? data.plan.length : 0,
        }
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const testAIDailyChat = async (): Promise<DebugResult> => {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    try {
      const profileResult = await PrivateProfileService.getProfile(user.uid);
      if (!profileResult.success || !profileResult.data) {
        return { success: false, error: 'Profile not found for AI test' };
      }

      const text = await mentorChatService.sendChatText(
        'What should I focus on today for personal growth?',
        profileResult.data,
        profileResult.data.chatHistory || [],
        mentorId || 'leadership_coach',
        { promptStyle: 'brief', responseStyle: 'short', modelPreference: 'standard' }
      );

      return {
        success: true,
        data: {
          responseReceived: !!text,
          messageLength: text.length,
          preview: text.slice(0, 120),
        }
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const testFirebaseFunctions = async (): Promise<DebugResult> => {
    try {
      // Test the getNextOnboardingPage function
      // This test remains as a placeholder; integrate when a callable is available
      const response = { data: { ok: true } } as any;

      return {
        success: true,
        data: {
          functionCalled: 'getNextOnboardingPage',
          responseReceived: !!response?.data,
          responseType: typeof response?.data,
        }
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  // === SYSTEM TESTS ===
  const testFirestoreConnection = async (): Promise<DebugResult> => {
    try {
      // Try to read from a public collection
      const testCollection = collection(firestore, 'books_app_configuration');
      const snapshot = await getDocs(testCollection);
      
      return {
        success: true,
        data: {
          connectionWorking: true,
          documentsFound: snapshot.size,
          firestoreProject: 'Connected',
        }
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const generateCategoryBookIds = async (): Promise<DebugResult> => {
    try {
      addLog('📚 Fetching all books from Firestore...');
      
      const booksCollection = collection(firestore, 'books_book');
      const booksQuery = query(booksCollection, where('languageCode', '==', 'en'));
      const snapshot = await getDocs(booksQuery);
      
      addLog(`📦 Loaded ${snapshot.size} books`);
      
      // Build category -> bookIds mapping
      const categoryMap: { [category: string]: string[] } = {};
      
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const bookId = doc.id.split('_')[0]; // Extract clean ID
        const categories = data.categories || {};
        
        // Add book to each of its categories
        Object.keys(categories).forEach((category) => {
          const normalizedCategory = category.toLowerCase().replace(/[\s_-]/g, '');
          
          if (!categoryMap[normalizedCategory]) {
            categoryMap[normalizedCategory] = [];
          }
          
          // Only add if not already in the list
          if (!categoryMap[normalizedCategory].includes(bookId)) {
            categoryMap[normalizedCategory].push(bookId);
          }
        });
      });
      
      // Limit each category to 40 books (as requested)
      Object.keys(categoryMap).forEach((category) => {
        categoryMap[category] = categoryMap[category].slice(0, 40);
      });
      
      // Sort categories by number of books (descending)
      const sortedCategories = Object.keys(categoryMap).sort(
        (a, b) => categoryMap[b].length - categoryMap[a].length
      );
      
      // Build output structure
      const output: { [key: string]: string[] } = {};
      sortedCategories.forEach((category) => {
        output[category] = categoryMap[category];
      });
      
      // Format as TypeScript code for easy copy-paste
      const tsCode = `export const CATEGORY_BOOK_IDS: { [category: string]: string[] } = ${JSON.stringify(output, null, 2)};`;
      
      addLog(`✅ Generated mapping for ${sortedCategories.length} categories`);
      addLog('📋 Copy the TypeScript code from the console below:');
      console.log('\n\n=== COPY THIS TO data/categoryBookIds.ts ===\n');
      console.log(tsCode);
      console.log('\n=== END OF CODE ===\n\n');
      
      return {
        success: true,
        data: {
          totalCategories: sortedCategories.length,
          categoriesWithMostBooks: sortedCategories.slice(0, 5).map(cat => ({
            category: cat,
            bookCount: categoryMap[cat].length
          })),
          message: 'Check console for full TypeScript code to copy'
        }
      };
    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
      return { success: false, error: error.message };
    }
  };

  const testResetOnboarding = async (): Promise<DebugResult> => {
    try {
      await resetOnboardingState();
      return {
        success: true,
        data: 'Onboarding state reset successfully'
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const clearSQLiteCache = async (): Promise<DebugResult> => {
    try {
      addLog('🧹 Clearing SQLite cache...');
      await sqliteCacheService.clearAll();
      return {
        success: true,
        data: 'All SQLite cache cleared successfully (books & insights)'
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  // === UI HELPERS ===
  const renderTestResult = (testName: string, result?: DebugResult) => {
    if (!result) return null;

    const icon = result.success ? '✅' : '❌';
    const color = result.success ? Theme.colors.primary : Theme.colors.error;
    
    return (
      <Card style={[styles.resultCard, { borderLeftColor: color }] as any}>
        <View style={styles.resultHeader}>
          <Text variant="body1" style={{ color }}>
            {icon} {testName}
          </Text>
          {result.duration && (
            <Text variant="caption" color="secondary">
              {result.duration}ms
            </Text>
          )}
        </View>
        
        {result.error && (
          <Text variant="body2" style={{ color: Theme.colors.error, marginTop: 4 }}>
            Error: {result.error}
          </Text>
        )}
        
        {result.data && (
          <View style={styles.resultData}>
            <Text variant="caption" color="secondary">Data:</Text>
            <Text variant="body2" style={styles.dataText}>
              {typeof result.data === 'string' 
                ? result.data 
                : JSON.stringify(result.data, null, 2)
              }
            </Text>
          </View>
        )}
      </Card>
    );
  };

  const runAllTests = async () => {
    addLog('🚀 Running all tests...');
    
    // Run tests in sequence
    await runTest('Auth Status', testAuthStatus);
    await runTest('Firestore Connection', testFirestoreConnection);
    await runTest('Profile Get', testProfileGet);
    await runTest('Firebase Functions', testFirebaseFunctions);
    
    // Only run profile create if no profile exists
    const profileResult = results['Profile Get'];
    if (!profileResult?.success) {
      await runTest('Profile Create', testProfileCreate);
    }
    
    await runTest('Profile Update', testProfileUpdate);
    await runTest('AI Onboarding', testAIOnboarding);
    await runTest('AI Daily Chat', testAIDailyChat);
    
    addLog('🏁 All tests completed');
  };

  const clearResults = () => {
    setResults({});
    setLogs([]);
    addLog('🧹 Results and logs cleared');
  };

  return (
    <PageContainer>
      {/* Compact close button */}
      <Pressable 
        style={styles.closeButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="close" size={24} color={Theme.colors.textPrimary} />
      </Pressable>
      
      <ScrollView style={styles.container}>
        <ContentContainer>
          <Text variant="heading2" style={styles.title}>
            🔧 Debug Console
          </Text>

          {/* Authentication Status */}
          <Card style={styles.statusCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Authentication Status
            </Text>
            
            <View style={styles.statusRow}>
              <Text variant="body1">Authenticated:</Text>
              <Text variant="body1" style={{
                color: authStatus.isAuthenticated ? Theme.colors.primary : Theme.colors.error
              }}>
                {authStatus.isAuthenticated ? '✅ Yes' : '❌ No'}
              </Text>
            </View>
            
            {authStatus.isAuthenticated && (
              <>
                <View style={styles.statusRow}>
                  <Text variant="body2" color="secondary">UID:</Text>
                  <Text variant="body2" style={styles.monoText}>{authStatus.uid}</Text>
                </View>
                <View style={styles.statusRow}>
                  <Text variant="body2" color="secondary">Email:</Text>
                  <Text variant="body2">{authStatus.email || 'Not set'}</Text>
                </View>
                <View style={styles.statusRow}>
                  <Text variant="body2" color="secondary">Verified:</Text>
                  <Text variant="body2">
                    {authStatus.emailVerified ? '✅ Yes' : '❌ No'}
                  </Text>
                </View>
                <View style={styles.statusRow}>
                  <Text variant="body2" color="secondary">Created:</Text>
                  <Text variant="body2" style={styles.monoText}>
                    {authStatus.creationTime ? new Date(authStatus.creationTime).toLocaleString() : 'Unknown'}
                  </Text>
                </View>
              </>
            )}
          </Card>

          {/* Automated Testing */}
          <Card style={styles.controlsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Automated Testing
            </Text>
            <Text variant="body2" color="secondary" style={{ marginBottom: Theme.spacing.md }}>
              Run comprehensive automated tests and view results
            </Text>
            <Button
              title="🧪 Run All Tests"
              onPress={() => navigation.navigate('TestRunner' as any)}
              style={{ marginBottom: Theme.spacing.sm }}
            />
            <Button
              title="🤖 Run Tests (Auto Mode)"
              onPress={() => navigation.navigate('TestRunner' as any, { autoTest: true })}
              variant="secondary"
            />
          </Card>

          {/* Manual Testing */}
          <Card style={styles.controlsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Manual Testing
            </Text>
            <Text variant="body2" color="secondary" style={{ marginBottom: Theme.spacing.md }}>
              Test OAuth flows (Apple/Google Sign-In) before deployment
            </Text>
            <Button
              title="🔐 OAuth Tests"
              onPress={() => navigation.navigate('ManualTests' as any)}
              variant="secondary"
            />
          </Card>

          {/* Specialized Debug Screens */}
          <Card style={styles.controlsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Specialized Debug Screens
            </Text>
            <Button
              title="💳 RevenueCat Debug"
              onPress={() => navigation.navigate('RevenueCatDebug' as any)}
              style={{ marginBottom: Theme.spacing.sm }}
            />
            <Button
              title="💬 Mentor Gateway Debug"
              onPress={() => navigation.navigate('MentorGatewayDebug' as any)}
              variant="secondary"
              style={{ marginBottom: Theme.spacing.sm }}
            />
            <Button
              title="💰 Test Paywall Flow"
              onPress={() => navigation.navigate('Paywall' as any)}
              variant="secondary"
            />
          </Card>

          {/* Data Generation Tools */}
          <Card style={styles.controlsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              📊 Data Generation
            </Text>
            <Text variant="body2" color="secondary" style={{ marginBottom: Theme.spacing.md }}>
              Generate hardcoded data structures from Firebase
            </Text>
            <Button
              title="📚 Generate Category Book IDs"
              onPress={() => runTest('Generate Category IDs', generateCategoryBookIds)}
              disabled={isLoading}
            />
            <Text variant="caption" color="secondary" style={{ marginTop: Theme.spacing.xs }}>
              Generates TypeScript code for hardcoded category mappings. Check console logs.
            </Text>
          </Card>

          {/* Cache Management */}
          <Card style={styles.controlsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              🗄️ Cache Management
            </Text>
            <Text variant="body2" color="secondary" style={{ marginBottom: Theme.spacing.md }}>
              Manage SQLite cache for books and insights
            </Text>
            <Button
              title="🧹 Clear All Cache"
              onPress={() => runTest('Clear SQLite Cache', clearSQLiteCache)}
              disabled={isLoading}
              variant="secondary"
            />
            <Text variant="caption" color="secondary" style={{ marginTop: Theme.spacing.xs }}>
              Removes all cached books and insights from the SQLite database.
            </Text>
          </Card>

          {/* Error Monitoring Tests */}
          <Card style={styles.controlsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              🚨 Error Monitoring (Sentry)
            </Text>
            <Text variant="body2" color="secondary" style={{ marginBottom: Theme.spacing.md }}>
              Test Sentry error reporting integration
            </Text>
            <Button
              title="🧪 Test Sentry Error"
              onPress={() => {
                addLog('🧪 Testing Sentry error capture...');
                Sentry.captureException(new Error('Test error from Debug Screen'));
                addLog('✅ Error sent to Sentry! Check your dashboard.');
              }}
              style={{ marginBottom: Theme.spacing.sm }}
            />
            <Button
              title="📊 Test with Context"
              onPress={() => {
                addLog('🧪 Testing Sentry error with context...');
                Sentry.setContext('debug_test', {
                  screen: 'DebugScreen',
                  userId: authStatus.uid,
                  timestamp: new Date().toISOString(),
                });
                Sentry.captureException(new Error('Test error with rich context'));
                addLog('✅ Error with context sent to Sentry!');
              }}
              variant="secondary"
            />
          </Card>

          {/* App Configuration */}
          <Card style={styles.controlsCard}>
            <Text variant="heading3" style={styles.sectionTitle}>
              A/B Tests Configuration
            </Text>
            
            {abTests.length === 0 ? (
              <Text variant="body2" color="secondary">No active A/B tests found.</Text>
            ) : (
              abTests.map((test) => (
                <View key={test.name} style={styles.testContainer}>
                  <Text variant="body1" style={styles.testName}>
                    {test.name} <Text variant="caption" color="secondary">(v{test.version})</Text>
                  </Text>
                  <Text variant="caption" color="secondary" style={styles.testKey}>
                    Key: {test.key}
                  </Text>
                  
                  <View style={styles.variantsGrid}>
                    {test.variants.map((variant: string) => (
                      <Button
                        key={variant}
                        title={variant}
                        variant={test.currentVariant === variant ? 'primary' : 'secondary'}
                        onPress={() => handleVariantChange(test.name, variant)}
                        style={styles.variantButton}
                        size="small"
                      />
                    ))}
                  </View>
                </View>
              ))
            )}
          </Card>


          {/* Test Results */}
          {Object.keys(results).length > 0 && (
            <Card style={styles.resultsCard}>
              <Text variant="heading3" style={styles.sectionTitle}>
                Test Results
              </Text>
              
              {Object.entries(results).map(([testName, result]) => (
                <View key={testName}>
                  {renderTestResult(testName, result)}
                </View>
              ))}
            </Card>
          )}

          {/* Logs */}
          {logs.length > 0 && (
            <Card style={styles.logsCard}>
              <Text variant="heading3" style={styles.sectionTitle}>
                Debug Logs
              </Text>
              
              <ScrollView style={styles.logsContainer} nestedScrollEnabled>
                {logs.map((log, index) => (
                  <Text key={index} variant="caption" style={styles.logEntry}>
                    {log}
                  </Text>
                ))}
              </ScrollView>
            </Card>
          )}

          {/* Loading Indicator */}
          {isLoading && (
            <Card style={styles.loadingCard}>
              <Text variant="body1" align="center">
                🔄 Running test...
              </Text>
            </Card>
          )}
        </ContentContainer>
      </ScrollView>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 16,
    zIndex: 1000,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  container: {
    flex: 1,
  },
  title: {
    marginBottom: Theme.spacing.xl,
    textAlign: 'center',
  },
  statusCard: {
    marginBottom: Theme.spacing.lg,
  },
  controlsCard: {
    marginBottom: Theme.spacing.lg,
  },
  resultsCard: {
    marginBottom: Theme.spacing.lg,
  },
  logsCard: {
    marginBottom: Theme.spacing.xl,
  },
  loadingCard: {
    marginBottom: Theme.spacing.xl,
    backgroundColor: Theme.colors.primaryLight,
  },
  formRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: Theme.spacing.md,
  },
  sectionTitle: {
    marginBottom: Theme.spacing.md,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Theme.spacing.xs,
  },
  buttonGrid: {
    flexDirection: 'row',
    gap: Theme.spacing.sm,
    marginBottom: Theme.spacing.sm,
  },
  gridButton: {
    flex: 1,
  },
  dangerZone: {
    marginTop: Theme.spacing.lg,
    paddingTop: Theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: Theme.colors.border,
  },
  dangerTitle: {
    marginBottom: Theme.spacing.sm,
    textAlign: 'center',
  },
  dangerButton: {
    backgroundColor: '#ff6b6b',
  },
  resultCard: {
    marginBottom: Theme.spacing.sm,
    borderLeftWidth: 4,
  },
  resultHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Theme.spacing.xs,
  },
  resultData: {
    marginTop: Theme.spacing.sm,
  },
  dataText: {
    fontFamily: 'monospace',
    fontSize: 12,
    backgroundColor: Theme.colors.background,
    padding: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.sm,
    marginTop: Theme.spacing.xs,
  },
  logsContainer: {
    maxHeight: 200,
    backgroundColor: Theme.colors.background,
    borderRadius: Theme.borderRadius.sm,
    padding: Theme.spacing.sm,
  },
  logEntry: {
    fontFamily: 'monospace',
    fontSize: 11,
    marginBottom: 2,
    color: Theme.colors.textSecondary,
  },
  monoText: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  specializedScreen: {
    marginBottom: Theme.spacing.lg,
  },
  specializedTitle: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    marginBottom: Theme.spacing.xs,
  },
  specializedDesc: {
    marginBottom: Theme.spacing.md,
  },
  specializedButton: {
    marginTop: Theme.spacing.sm,
  },
  testContainer: {
    marginBottom: Theme.spacing.lg,
    paddingBottom: Theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Theme.colors.border,
  },
  testName: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    marginBottom: 2,
  },
  testKey: {
    marginBottom: Theme.spacing.sm,
    fontSize: 10,
  },
  variantsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Theme.spacing.xs,
  },
  variantButton: {
    minWidth: 80,
  },
});

export default DebugScreen; 